import React, { lazy, Suspense, useCallback, useContext } from 'react';
import type { AudiHeaderDealerSearchProps } from '../../interfaces/header-components.interfaces';
import {
  HeaderStyledDealerButton,
  HeaderStyledDealerDisplay,
  HeaderStyledDealerLabel,
  HeaderStyledDealerLink,
  HeaderStyledDealerName,
  HeaderStyledDealerTextWrapper,
} from './AudiHeaderDealerContextStyles';
import { Icon, Layout } from '@oneaudi/unified-web-components';
import {
  AdditionalDealerData,
  AdditionalDealerDataContent,
} from './AudiHeaderAdditionalDealerDataStyles';
import AudiSearchFeatureAppLoader from '../AudiHeaderSearch/AudiSearchFeatureAppLoader';
import { DEALER_CONTEXT_UNSUBSCRIBE, KEY_DEALER_DATA } from '../../utils/globals';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';
import DealerSearchIntro from './DealerSearchIntro';
import { ConfigContext } from '../../context/ConfigContext';
import useToggle from '../../hooks/use-toggle';

const DealerSearchFeatureApp = lazy(() => import('./DealerSearchFeatureApp'));

const DealerSearchMobile: React.FC<AudiHeaderDealerSearchProps> = ({
  isMobileView,
  headerNavigationItemsAmount,
  dealerSearchGeolocationEnabled,
  onClickDealerSearchContext,
  showDealerSearch,
}) => {
  const { dealerSearchBenefitEnabled } = useContext(ConfigContext);

  const storageDealerData =
    typeof window !== 'undefined' && sessionStorage?.getItem(KEY_DEALER_DATA);
  const showDealerSearchFA = !dealerSearchBenefitEnabled || showDealerSearch;
  const buttonLabel = useI18n(messages.dealership_select_button);

  const [toggleElement, toggle] = useToggle();

  const handleDealerSearchContext = useCallback(() => {
    onClickDealerSearchContext();
  }, [onClickDealerSearchContext]);

  // auto load dealer search
  if (dealerSearchGeolocationEnabled && storageDealerData !== DEALER_CONTEXT_UNSUBSCRIBE) {
    return (
      <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" />}>
        <DealerSearchFeatureApp
          dealerSearchGeolocationEnabled={dealerSearchGeolocationEnabled}
          visible={false}
          isMobileView={isMobileView}
        />
      </Suspense>
    );
  }

  if (!dealerSearchGeolocationEnabled) {
    const poiIcon = <Icon name="poi" size="s" />;
    const arrowIcon = toggleElement ? (
      <Icon name="arrow-up" size="s" />
    ) : (
      <Icon name="arrow-down" size="s" />
    );

    return (
      <HeaderStyledDealerDisplay
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        isMobileView
      >
        <HeaderStyledDealerButton
          isMobileView
          onClick={() => {
            toggle();
          }}
        >
          <HeaderStyledDealerTextWrapper>
            <Layout justify="flex-end" align="flex-end" margin={[0, '12px', 0, 0]}>
              {poiIcon}
            </Layout>
            <HeaderStyledDealerLink>
              <HeaderStyledDealerLabel isMobileView>
                <span>{buttonLabel}</span>
              </HeaderStyledDealerLabel>
              <HeaderStyledDealerName isMobileView>{buttonLabel}</HeaderStyledDealerName>
            </HeaderStyledDealerLink>
          </HeaderStyledDealerTextWrapper>
          <Layout justify="flex-end" align="flex-end">
            {arrowIcon}
          </Layout>
        </HeaderStyledDealerButton>
        <AdditionalDealerData
          open={toggleElement}
          isDealerSearch
          isMobileView
          role="tabpanel"
          aria-hidden={!showDealerSearch}
          className="dealer-container"
        >
          <AdditionalDealerDataContent isMobileView isDealerSearch>
            {showDealerSearchFA ? (
              <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" />}>
                <DealerSearchFeatureApp
                  dealerSearchGeolocationEnabled={false}
                  visible
                  isMobileView={isMobileView}
                />
              </Suspense>
            ) : dealerSearchBenefitEnabled ? (
              <DealerSearchIntro handleSetShowDealerSearch={handleDealerSearchContext} />
            ) : null}
          </AdditionalDealerDataContent>
        </AdditionalDealerData>
      </HeaderStyledDealerDisplay>
    );
  } else {
    return null;
  }
};
export default DealerSearchMobile;
